import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, UserPlus, ShieldCheck, AlertTriangle } from "lucide-react";
import { BrandLink } from "@/components/Brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvitePage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/team/public/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Invite invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Pick a password of at least 6 characters"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/team/public/invite/${token}/accept`, {
        password, name: name.trim() || null,
      });
      localStorage.setItem("token", r.data.token);
      localStorage.setItem("user", JSON.stringify(r.data.user));
      toast.success(`Welcome to ${r.data.user.workshop_name}!`);
      nav("/dashboard");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't accept invite");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="accept-invite-page">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <BrandLink />
          <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground" data-testid="accept-invite-signin-link">
            Already have an account? Sign in
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          {!invite && !err && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground justify-center" data-testid="accept-invite-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading invite…
            </div>
          )}

          {err && (
            <div className="card-default p-6 text-center" data-testid="accept-invite-error">
              <AlertTriangle className="w-10 h-10 text-destructive mx-auto" />
              <h1 className="font-display text-xl font-black mt-3">Invite unavailable</h1>
              <p className="text-sm text-muted-foreground mt-2">{err}</p>
              <Link to="/login" className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground font-bold text-sm">
                Go to sign-in
              </Link>
            </div>
          )}

          {invite && (
            <div className="card-default p-6 md:p-8" data-testid="accept-invite-form">
              <div className="text-center">
                <div className="inline-flex w-12 h-12 rounded-full bg-primary/15 items-center justify-center">
                  <UserPlus className="w-6 h-6 text-primary" />
                </div>
                <div className="label-overline mt-3">You're invited to join</div>
                <h1 className="font-display text-3xl font-black mt-1">
                  {invite.workshop_name}
                </h1>
                <p className="text-sm text-muted-foreground mt-2">
                  {invite.invited_by_name} has added you as <strong className="text-foreground">{invite.role}</strong>.
                </p>
              </div>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <label className="block text-sm">
                  <span className="font-semibold">Email</span>
                  <input
                    value={invite.email}
                    disabled
                    className="mt-1 w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
                    data-testid="accept-invite-email"
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-semibold">Your name</span>
                  <input
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Tom Mechanic"
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    data-testid="accept-invite-name-input"
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-semibold">Choose a password</span>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    data-testid="accept-invite-password-input"
                  />
                </label>

                <button
                  type="submit"
                  disabled={busy || !name || password.length < 6}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground font-bold disabled:opacity-50"
                  data-testid="accept-invite-submit"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Accept invite & sign in
                </button>
              </form>

              <div className="mt-5 rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs text-muted-foreground flex items-start gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                <span>Your password is hashed with bcrypt. Only the workshop owner can change your role or remove you.</span>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
